import type { CanvasSelectionSemanticRole } from '@streamlain/canvas-selection-contract';
import type {
  UiObjectRef,
  UiSemanticElement,
  WorkspaceContextIssue,
  WorkspaceViewReadResult,
  WorkspaceViewRevision,
  WorkspaceViewScope,
  WorkspaceViewSnapshot,
} from '@streamlain/copilot-realtime-contract';
import type { PreviewSelectionScope } from '@streamlain/view-config';

import type { AIError } from '../../provider';
import type { MailMessageAttachment } from '../../utils/mail-selection-context';
import type { ChatStatus, HistoryMessage } from '../ai-chat-messages';

export interface ExternalChatContextDoc {
  docId: string;
  docContent: string;
  docTitle?: string;
  tags?: string;
  createDate?: string;
  updatedDate?: string;
}

// sl-57s: Database Attachment Types
export type DatabaseAttachmentPriority = 'highest' | 'mid' | 'lowest';
export type DatabaseAttachmentSource = 'paste' | 'fullscreen' | 'focus';
export type WorkflowAttachmentSource = DatabaseAttachmentSource | 'created';
export type CanvasSelectionPriority = DatabaseAttachmentPriority | 'high';
export type CanvasSelectionSource = DatabaseAttachmentSource | 'client-action';
export type DatabaseAttachmentScope =
  | 'metadata-only'
  | 'visible-window'
  | 'selected-rows';

export interface DatabaseVisibleRowWindow {
  offset: number;
  limit: number;
  totalCount: number;
  rowIds: string[];
  hasMore: boolean;
}

export interface DatabaseAttachment {
  docId?: string;
  blockId: string;
  name: string;
  columns: DatabaseColumn[];
  bounds?: CanvasSelectionBounds;
  source: DatabaseAttachmentSource;
  priority: DatabaseAttachmentPriority;
  scope?: DatabaseAttachmentScope;
  activeViewId?: string;
  activeViewType?: string;
  activeFilterCount?: number;
  activeSortCount?: number;
  visibleRowWindow?: DatabaseVisibleRowWindow;
  /** Columns actually projected into AI reads, ordered by user-visible priority. */
  projectedColumnIds?: string[];
  /** Full schema size for exact UI/backend disclosure. */
  totalColumnCount?: number;
  // sl-28ek: Selected rows for focused AI context
  selectedRowIds?: string[];
  selectedRowLabel?: string; // e.g. "Rows 1, 3-5, 8" for chip display
}

export interface DatabaseColumn {
  id: string;
  name: string;
  type: string;
  data?: Record<string, unknown>;
}

// Automation Attachment Types
export interface AutomationAttachment {
  docId?: string;
  blockId: string;
  name: string;
  bounds?: CanvasSelectionBounds;
  sourceTableId: string | null;
  sourceTableName: string | null;
  workflowId: string | null;
  source: DatabaseAttachmentSource;
  priority: DatabaseAttachmentPriority;
}

// sl-w252: PDF Attachment Types (Copilot PDF Context)
export interface PdfAttachment {
  file: File;
  name: string;
  pageCount: number;
  thumbnailDataUrl: string | null;
  source: 'paste';
}

// sl-w252: PDF Context Attachment (Canvas Selection — Weg B)
export interface PdfContextAttachment {
  docId: string;
  blockId: string;
  name: string;
  /** Present for live client selection only; never persisted in new messages. */
  blobKey?: string;
  source: DatabaseAttachmentSource;
  priority: DatabaseAttachmentPriority;
}

export interface CanvasSelectionBounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface CanvasSelectionItem {
  id: string;
  kind: 'canvas_element' | 'block';
  type: string;
  role?: CanvasSelectionSemanticRole;
  label: string;
  text?: string | null;
  bounds?: CanvasSelectionBounds;
  parentId?: string | null;
  conversationParentId?: string | null;
  depth?: number;
  childCount?: number;
  groupId?: string | null;
  children?: CanvasSelectionItem[];
}

export type CanvasAIChatContextMode = 'lineage-only' | 'selection-analysis';

export interface CanvasSelectionAttachment {
  docId: string;
  label: string;
  elementIds: string[];
  blockIds: string[];
  items: CanvasSelectionItem[];
  source: CanvasSelectionSource;
  priority: CanvasSelectionPriority;
}

// sl-dpq2.5: Workflow Attachment Types (Edit with AI context)
export interface WorkflowAttachment {
  workflowId: string;
  /** Automation selection that implicitly contributed this workflow context. */
  automationBlockId?: string;
  workflowName?: string;
  /** Exact semantic revision shown when the context was attached. */
  version?: number | null;
  versionKey?: string;
  versionRole?: 'draft' | 'published' | 'snapshot';
  access?: 'editable' | 'read-only';
  workingCopyRevision?: string | null;
  baseSnapshotVersion?: number | null;
  scope?: 'whole-workflow' | 'selected-subgraph';
  /** Stable workflow+version identity used to replace the automatic pill. */
  contextKey?: string;
  /** UI-only marker: active builder context rather than a manual attachment. */
  autoSelected?: boolean;
  selectedNodes: Array<{
    name: string;
    type: string;
    slug: string;
    position: [number, number];
    parameters: Record<string, unknown>;
    disabled?: boolean;
  }>;
  haloNodes: Array<{
    name: string;
    type: string;
    slug: string;
  }>;
  edges: Array<{
    source: string;
    sourceOutput: number;
    target: string;
    targetInput: number;
  }>;
  tokenEstimate: number;
  source: WorkflowAttachmentSource;
  priority: DatabaseAttachmentPriority;
}

/** Automatic UI context holds a native revision-bound reader, never fake body arrays. */
export interface WorkflowContextReference extends Omit<
  WorkflowAttachment,
  'selectedNodes' | 'haloNodes' | 'edges' | 'tokenEstimate'
> {
  kind: 'workflow-context-reference';
  /** Native selected IDs, or raw node count when the whole workflow is active. */
  selectionCount: number;
  /** Native owner metadata and readers remain UI-only, outside the attachment wire. */
  nativeBinding?: Omit<
    Extract<NativeContextBinding, { kind: 'workflow' }>,
    'nodeIds'
  >;
  sourceGeneration?: string;
  /** Exact native automatic publication; UI-only, outside message attachments. */
  sourceRevision?: WorkspaceViewRevision;
  captureSelection?(
    ids: readonly string[]
  ): Extract<ChatContextSelection, { kind: 'native' }>;
  materializeWithPinnedIds?(
    signal: AbortSignal,
    ids: readonly string[]
  ): Promise<WorkflowAttachment>;
  materialize(signal: AbortSignal): Promise<WorkflowAttachment>;
  assertCurrent(): void;
}

export type WorkflowContextAttachment =
  | WorkflowAttachment
  | WorkflowContextReference;

export function isWorkflowContextReference(
  attachment: WorkflowContextAttachment
): attachment is WorkflowContextReference {
  return (
    'kind' in attachment && attachment.kind === 'workflow-context-reference'
  );
}

export interface CodeSelectionAttachment {
  id: string;
  title: string;
  surface: 'workflow-code' | 'app-base-css' | 'theme-css';
  language: 'javascript' | 'python' | 'json' | 'css' | 'text';
  selectedText: string;
  fullText: string;
  lineText: string;
  from: number;
  to: number;
  fromLine: number;
  toLine: number;
  source: DatabaseAttachmentSource;
  priority: DatabaseAttachmentPriority;
}

export type ChatContextValue = {
  /** Small UI-only references. Never persisted in messages, drafts or wire. */
  contextSelections: readonly ChatContextSelection[];
  // history messages of the chat
  messages: HistoryMessage[];
  status: ChatStatus;
  // a question to the user is open in the chat input (tool, target or goal confirmation)
  awaitingConfirmation: boolean;
  error: AIError | null;
  // plain-text of the selected content
  quote: string;
  // markdown of the selected content
  markdown: string;
  // images of the selected content or user uploaded
  images: File[];
  // snapshot of the selected content
  snapshot: string | null;
  // attachments of the selected content
  attachments: { sourceId: string; name: string }[];
  // combined markdown of the selected elements
  combinedElementsMarkdown: string | null;
  // docs of the selected content
  docs: string[];
  // html of make it real
  html: string | null;
  abortController: AbortController | null;
  // sl-57s: database attachments with priority
  databaseAttachments: DatabaseAttachment[];
  // automation attachments with priority
  automationAttachments: AutomationAttachment[];
  // sl-dpq2.5: workflow attachments (Edit with AI context)
  workflowAttachments: WorkflowContextAttachment[];
  // CodeMirror selections from workflow/site CSS editors.
  codeSelections: CodeSelectionAttachment[];
  // selected mails from the fullscreen mail app.
  mailAttachments?: MailMessageAttachment[];
  // sl-w252: PDF attachments (Paste — Weg A, Vision input)
  pdfAttachments: PdfAttachment[];
  // sl-w252: PDF context attachments (Canvas Selection — Weg B, RAG)
  pdfContextAttachments: PdfContextAttachment[];
  // Canvas object selection context for surgical Copilot edits
  canvasSelections: CanvasSelectionAttachment[];
};

export type ChatContextAttachmentPatch = Partial<
  Pick<
    ChatContextValue,
    | 'quote'
    | 'markdown'
    | 'images'
    | 'snapshot'
    | 'attachments'
    | 'combinedElementsMarkdown'
    | 'docs'
    | 'html'
    | 'databaseAttachments'
    | 'automationAttachments'
    | 'workflowAttachments'
    | 'codeSelections'
    | 'mailAttachments'
    | 'pdfAttachments'
    | 'pdfContextAttachments'
    | 'canvasSelections'
  >
>;

export type NativeContextBinding =
  | {
      kind: 'doc' | 'canvas';
      workspaceId: string;
      docId: string;
      storeGeneration: string;
      objectIds: readonly string[];
    }
  | {
      kind: 'workflow';
      workspaceId: string;
      workflowId: string;
      version: number | null;
      modelRevision: number;
      nodeIds: readonly string[];
    };
export interface UiContextBinding extends WorkspaceViewScope {
  sourceKind: 'main' | 'dialog';
  ownerId: string;
  ownerGeneration: string;
}
export interface WorkspaceViewSelection {
  refs: readonly UiObjectRef[];
  elements: readonly UiSemanticElement[];
}
/**
 * Where the active view stands, read without a snapshot: its workbench route
 * and the sources that hold what the user captured in it.
 */
export type WorkspaceViewPlace = {
  workspaceId: string;
  viewId: string;
  route: string;
  captured: readonly string[];
  revision?: WorkspaceViewRevision;
  sources?: readonly string[];
};
export type WorkspaceViewContextReader = ((
  selection?: WorkspaceViewSelection
) => WorkspaceViewReadResult) & {
  place: () => WorkspaceViewPlace | null;
  collect?: (
    selection: WorkspaceViewSelection | undefined,
    signal: AbortSignal
  ) => Promise<WorkspaceViewReadResult>;
  /** Wait for the current view's rendering to settle; false means it navigated. */
  settle?: (signal: AbortSignal) => Promise<boolean>;
  isCurrent?: (snapshot: WorkspaceViewSnapshot) => boolean;
};
export interface ChatContextSelectionBase {
  key: string;
  sourceKey: string;
  provenance: 'automatic' | 'explicit';
  label: string;
  scope: WorkspaceViewScope;
  sourceGeneration: string;
  assertCurrent(): void;
  /** Suppression is metadata in the existing context value, not a payload cache. */
  revision?: WorkspaceViewRevision;
  dismissed?: boolean;
}
export type ChatContextSelection = ChatContextSelectionBase &
  (
    | {
        kind: 'native';
        binding: NativeContextBinding;
        /** Send-local union of IDs from the same native owner, materialized once. */
        materialize(
          signal: AbortSignal,
          ids?: readonly string[],
          assertSendCurrent?: () => void
        ): Promise<ChatContextAttachmentPatch>;
      }
    | {
        kind: 'ui' | 'preview';
        binding: UiContextBinding | PreviewSelectionScope;
        ref: UiObjectRef;
        admittedSequence: number;
        read(signal: AbortSignal): Promise<WorkspaceViewSelection>;
      }
  );
export interface ChatContextSendRead {
  context: ChatContextValue;
  workspaceView: WorkspaceViewReadResult | undefined;
  assertCurrent(): void;
}
export interface ChatContextSendPreparation {
  context: ChatContextValue;
  assertCurrent(): void;
  /** Fresh WORKSPACE_VIEW projection after the existing async send preparation. */
  readForSend(): Promise<ChatContextSendRead>;
}
export interface ChatContextManagerPort {
  applyAutomatic(
    sourceKey: string,
    revision: WorkspaceViewRevision,
    patch: ChatContextAttachmentPatch,
    selections: readonly ChatContextSelection[]
  ): void;
  addExplicit(
    selection: ChatContextSelection
  ): { ok: true } | { ok: false; reason: WorkspaceContextIssue };
  remove(keys: readonly string[]): void;
  /** Whether an active (not dismissed) selection with this key exists. */
  isSelected(key: string): boolean;
  invalidateSource(sourceKey: string, generation: string): void;
  /** Returns how many still active (not dismissed) selections were removed. */
  invalidateScope(scope: WorkspaceViewScope): number;
  prepareForSend(
    signal: AbortSignal,
    getReader?: () => WorkspaceViewContextReader | undefined
  ): Promise<ChatContextSendPreparation>;
  readForSend(signal: AbortSignal): Promise<ChatContextSendRead>;
}

/** Presentation/admission on the existing workspace host; no selected payload. */
export interface ChatContextPickingControl {
  readonly scope: WorkspaceViewScope;
  readonly shortcut?: {
    getBinding(): string | null;
    subscribe(listener: (binding: string | null) => void): () => void;
  };
  canToggle(): boolean;
  isEnabled(): boolean;
  subscribe(listener: (enabled: boolean) => void): () => void;
  setEnabled(
    enabled: boolean
  ): { ok: true } | { ok: false; reason: 'inactive-view' | 'unavailable' };
  bindContext(manager: ChatContextManagerPort): () => void;
}
